import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * HIU API — ABDM Milestone 3 endpoints (Health Information User)
 *
 * ArogyaKhata pulling a patient's external health history from other
 * facilities under consent — the mirror of the M1/M2 ABHA/HIP flows.
 *
 * Design rules enforced here (same as abhaApi.js):
 *   1. Every function returns a normalized domain-shaped object.
 *      Components never see axios response envelopes or raw backend keys.
 *   2. Errors carry declarative metadata (retryable) so UI layers become
 *      conditional on flags, not string-matched messages.
 *   3. X-Correlation-Id is injected by the apiClient interceptor, not here.
 *   4. No URL construction outside this file.
 *
 * Base: /api/organisation/hiu/
 *
 * Consent lifecycle: requestConsent → listConsents / getConsent → checkConsentStatus
 * Health-info fetch: requestHealthInfo → getHealthInfo (poll) → getHealthInfoRecords
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/hiu';

// ── Error handling ────────────────────────────────────────────────────

/**
 * HIU error classification. Backend errors here are mostly readable
 * ABDMConsentError messages (consent not found / not grantable / a request
 * already in progress) — simpler surface than ABHA's OTP flow, so this is
 * a leaner version of abhaApi.js's _buildError, same shape.
 */
function _buildError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  // Contract error envelope: new errors are {detail:{code,message,field?}}, old ones a string.
  const structured = detail && typeof detail === 'object' ? detail : null;
  const message = typeof detail === 'string' ? detail : structured?.message || 'HIU request failed';

  let code = 'UNKNOWN';
  let retryable = false;
  if (structured?.code) {
    // CONSENT_NOT_ACTIVE (403), ABDM_NOT_ACKNOWLEDGED (409), CONSENT_REQUEST_INVALID (422), ...
    code = structured.code;
    retryable = status >= 500;
  } else if (status === 403) {
    code = 'FORBIDDEN';
  } else if (!status) {
    code = 'NETWORK_ERROR';
    retryable = true;
  } else if (status === 404) {
    code = 'NOT_FOUND';
  } else if (status === 400) {
    code = 'VALIDATION_ERROR';
  } else if (status >= 500) {
    code = 'SERVICE_UNAVAILABLE';
    retryable = true;
  }

  const err = new Error(message);
  err.code = code;
  err.status = status;
  err.retryable = retryable;
  err.userMessage = message;
  if (structured?.field) err.field = structured.field;
  return err;
}

// ── Consent lifecycle ───────────────────────────────────────────────────

/**
 * Initiate a consent request for a patient's external history.
 *
 * @param {{ patientAbhaAddress: string, patientName: string, purposeCode?: string,
 *   hiTypes?: string[], dateFrom: string, dateTo: string, dataEraseAt: string,
 *   requestExpiresInMinutes?: number (whole minutes 2..1440 until WE expire an unanswered request; server adds it to its own clock; default 1440),
 *   permissionAccessMode?: string, requesterMemberId?: string (required unless caller is a doctor) }} params
 * @returns {{ consentRequestId, status }}
 */
export async function requestConsent({
  patientAbhaAddress, patientName, purposeCode = 'CAREMGT', hiTypes = [],
  dateFrom, dateTo, dataEraseAt, permissionAccessMode = 'VIEW',
  requesterMemberId, requestExpiresInMinutes,
}) {
  try {
    const { data } = await apiClient.post(`${BASE}/consent/request`, {
      patient_abha_address: patientAbhaAddress,
      patient_name: patientName,
      purpose_code: purposeCode,
      hi_types: hiTypes,
      date_from: dateFrom,
      date_to: dateTo,
      data_erase_at: dataEraseAt,
      request_expires_in_minutes: requestExpiresInMinutes, // FT-R6: omitted from the JSON when undefined
      permission_access_mode: permissionAccessMode,
      requester_member_id: requesterMemberId, // R71: server fills name + reg no from this doctor
    });
    return { consentRequestId: data.consent_request_id, status: data.status };
  } catch (err) {
    throw _buildError(err);
  }
}

/** Fields shared by list items and detail (contract 2.1 / 2.2). */
function _mapConsentCommon(c) {
  return {
    status: c.status,
    abdmStatus: c.abdm_status,
    displayStatus: c.display_status,
    patientAbhaAddress: c.patient_abha_address,
    patientName: c.patient_name,
    createdAt: c.created_at,
    expiresAt: c.expires_at,
    dateFrom: c.date_from,
    dateTo: c.date_to,
    requestedDateFrom: c.requested_date_from,
    requestedDateTo: c.requested_date_to,
    statusChangedAt: c.status_changed_at,
    grantedAt: c.granted_at,
    deniedAt: c.denied_at,
    revokedAt: c.revoked_at,
    expiredAt: c.expired_at,
    // FT-R6: when WE expire this request if the patient has not answered (null on rows from before this).
    // Not `requested_expires_at`: that key is the requested access-until (`requestedExpiresAt`, below).
    requestExpiryAt: c.request_expires_at ?? null,
    failureReason: c.failure_reason,
    // FT-R7: what was asked for. hi_types = what the patient granted once known, else what was requested.
    // All absent-safe: rows (or an older server) without them read as empty / null and show "—".
    hiTypes: c.hi_types || [],
    requestedHiTypes: c.requested_hi_types || [],
    purposeCode: c.purpose_code || '',
    requestedExpiresAt: c.requested_expires_at ?? null,
    // Already masked by the server (91-XXXX-XXXX-1234) - shown as it is; null when none was saved.
    patientAbhaNumberMasked: c.patient_abha_number_masked ?? null,
    // "Not sent" (SUBMIT_FAILED) reason code, e.g. ABDM_REJECTED, + ABDM's own error code.
    submitFailureCode: c.submit_failure_code ?? null,
    abdmErrorCode: c.abdm_error_code ?? null,
    recordsReceived: c.records_received ?? 0,
  };
}

/**
 * All consent requests for the org, newest first (HIU_FLOW_104 listing).
 * Tab counts and search are derived client-side from this list.
 *
 * @returns {Array<{ consentRequestId, status, abdmStatus, displayStatus, patientAbhaAddress,
 *   patientName, createdAt, expiresAt, dateFrom, dateTo, requestedDateFrom, requestedDateTo, statusChangedAt, grantedAt, deniedAt, revokedAt,
 *   expiredAt, requestExpiryAt, failureReason, hiTypes, requestedHiTypes, purposeCode, requestedExpiresAt,
 *   patientAbhaNumberMasked, submitFailureCode, abdmErrorCode, recordsReceived }>}
 */
export async function listConsents() {
  try {
    const { data } = await apiClient.get(`${BASE}/consent`);
    return (data.consents || []).map((c) => ({
      consentRequestId: c.consent_request_id,
      ..._mapConsentCommon(c),
    }));
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Consent detail — list fields plus the effective (granted) scope, what was requested,
 * and the latest fetch pointer for refresh continuity.
 *
 * A grant across several hospitals has one artefact per hospital (HIP): `artefacts` lists
 * each with its hospital, its own granted scope and its latest fetch. The top-level scope
 * is the union across hospitals.
 *
 * @param {string} consentId
 * @returns {{ consentId, consentArtefactIds, abdmConsentRequestId, purposeCode, hiTypes,
 *   dateFrom, dateTo, requestedHiTypes, requestedDateFrom, requestedDateTo,
 *   requestedExpiresAt, grantedScopeReceived, latestFetchId,
 *   artefacts: Array<{ artefactId, hipId, hipName, scopeReceived, hiTypes, dateFrom,
 *     dateTo, expiresAt, latestFetchId, fetchStatus, recordsReceived, recordsNamed,
 *     failureCode, failureReason }> } & list fields}
 */
export async function getConsent(consentId) {
  try {
    const { data } = await apiClient.get(`${BASE}/consent/${encodeURIComponent(consentId)}`);
    return {
      consentId: data.consent_id,
      ..._mapConsentCommon(data),
      consentArtefactIds: data.consent_artefact_ids || [],
      abdmConsentRequestId: data.abdm_consent_request_id,
      grantedScopeReceived: !!data.granted_scope_received,
      latestFetchId: data.latest_fetch_id,
      artefacts: (data.artefacts || []).map((a) => ({
        artefactId: a.artefact_id,
        hipId: a.hip_id,
        hipName: a.hip_name,
        scopeReceived: !!a.scope_received,
        hiTypes: a.hi_types || [],
        dateFrom: a.date_from,
        dateTo: a.date_to,
        expiresAt: a.expires_at,
        latestFetchId: a.latest_fetch_id,
        isOwnOrganisation: !!a.is_own_organisation,
        // NOT_REQUESTED | REQUESTED | RECEIVING | RECEIVED | FAILED
        fetchStatus: a.fetch_status || 'NOT_REQUESTED',
        recordsReceived: a.records_received ?? 0,
        requestedAt: a.requested_at || null,
        lastCheckedAt: a.last_checked_at || null,
        failureReason: a.failure_reason || null, // deprecated server-side: use failureCode
        // TIMEOUT | ABDM_REFUSED | NOTHING_SENT | CONSENT_ENDED | PUSH_WINDOW_CLOSED | DECRYPT_FAILED
        failureCode: a.failure_code || null,
        recordsNamed: a.records_named ?? null,
      })),
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Ask ABDM for the current state of a consent (R7 recovery path).
 *
 * @param {string} consentId
 * @returns {{ consentId, action: 'STATUS_REQUESTED'|'ARTEFACT_FETCH_REQUESTED'|'NONE',
 *   status, displayStatus }}
 */
export async function checkConsentStatus(consentId) {
  try {
    const { data } = await apiClient.post(
      `${BASE}/consent/${encodeURIComponent(consentId)}/check-status`
    );
    return {
      consentId: data.consent_id,
      action: data.action,
      status: data.status,
      displayStatus: data.display_status,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

// ── Health-information fetch ────────────────────────────────────────────

/**
 * Request records once consent is GRANTED. One request fetches from every hospital the
 * patient shared (one fetch per artefact); fetchId/status are the first fetch's.
 *
 * @param {{ consentArtefactId: string, dateFrom: string, dateTo: string,
 *   hiTypes?: string[], purposeCode?: string }} params
 * @returns {{ fetchId, status, fetches: Array<{ fetchId, status, artefactId }> }}
 */
export async function requestHealthInfo({
  consentArtefactId, dateFrom, dateTo, hiTypes = [], purposeCode,
}) {
  try {
    const { data } = await apiClient.post(`${BASE}/health-info/request`, {
      consent_artefact_id: consentArtefactId,
      date_from: dateFrom,
      date_to: dateTo,
      hi_types: hiTypes,
      purpose_code: purposeCode,
    });
    return {
      fetchId: data.fetch_id,
      status: data.status,
      fetches: (data.fetches || []).map((f) => ({
        fetchId: f.fetch_id, status: f.status, artefactId: f.artefact_id,
      })),
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Fetch progress (status, not content) — poll this while non-terminal.
 *
 * @param {string} fetchId
 * @returns {{ fetchId, artefactId, status, receivedCareContexts, failedCareContexts }}
 */
export async function getHealthInfo(fetchId) {
  try {
    const { data } = await apiClient.get(`${BASE}/health-info/${fetchId}`);
    return {
      fetchId: data.fetch_id,
      artefactId: data.artefact_id,
      status: data.status,
      receivedCareContexts: data.received_care_contexts || [],
      failedCareContexts: data.failed_care_contexts || [],
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Parsed content of every record fetched so far — the actual clinical data.
 *
 * @param {string} fetchId
 * @returns {Array<{ recordId, careContextReference, hiType, parsedSummary, createdAt,
 *   documentReferences: Array<{ id, title, contentType, hasData }> }>}
 */
export async function getHealthInfoRecords(fetchId) {
  try {
    const { data } = await apiClient.get(`${BASE}/health-info/${fetchId}/records`);
    return (data.records || []).map((r) => ({
      recordId: r.record_id,
      careContextReference: r.care_context_reference,
      hiType: r.hi_type,
      parsedSummary: r.parsed_summary,
      createdAt: r.created_at,
      // F7 — metadata only (never attachment bytes), lifted from parsed_summary's
      // own nested document_references key to a top-level, camelCased convenience
      // field for the viewer. hasData gates whether an attachment is actually
      // downloadable (a url-only/metadata-only reference has nothing to fetch).
      documentReferences: (r.parsed_summary?.document_references || []).map((d) => ({
        id: d.id,
        title: d.title,
        contentType: d.content_type,
        hasData: d.has_data,
      })),
    }));
  } catch (err) {
    throw _buildError(err);
  }
}

// ── Attachments (F7) ─────────────────────────────────────────────────────

/**
 * Fetch one DocumentReference attachment's bytes as a blob, ready for the global
 * FileViewerModal (useFileViewerStore.openViewer({ blobUrl, fileType, title })).
 * Backend proxy — no presigned URL is ever exposed to the client, mirroring
 * visitMetadataApi.js's downloadFileSecure pattern exactly.
 *
 * @param {string} fetchId
 * @param {string} recordId
 * @param {string} documentReferenceId
 * @returns {{ blobUrl: string, contentType: string }}
 */
export async function downloadAttachment(fetchId, recordId, documentReferenceId) {
  try {
    // SEC-019: documentReferenceId is sourced from an external HIP's FHIR
    // DocumentReference.id (untrusted) — encode every path segment before
    // interpolation, not just this one, since they all land directly in the URL.
    const response = await apiClient.get(
      `${BASE}/health-info/${encodeURIComponent(fetchId)}/records/` +
        `${encodeURIComponent(recordId)}/attachments/${encodeURIComponent(documentReferenceId)}`,
      { responseType: 'blob' }
    );
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const typedBlob = new Blob([response.data], { type: contentType });
    const blobUrl = URL.createObjectURL(typedBlob);
    return { blobUrl, contentType };
  } catch (err) {
    throw _buildError(err);
  }
}

// ── Raw FHIR bundle (R12) ────────────────────────────────────────────────

/**
 * Raw FHIR bundle of one fetched record (the "View raw data" toggle).
 *
 * @param {string} fetchId
 * @param {string} recordId
 * @returns {{ fetchId, recordId, careContextReference, hiType, bundle }}
 */
export async function getRawRecord(fetchId, recordId) {
  try {
    const { data } = await apiClient.get(
      `${BASE}/health-info/${encodeURIComponent(fetchId)}/records/${encodeURIComponent(recordId)}/raw`
    );
    return {
      fetchId: data.fetch_id,
      recordId: data.record_id,
      careContextReference: data.care_context_reference,
      hiType: data.hi_type,
      bundle: data.bundle,
    };
  } catch (err) {
    throw _buildError(err);
  }
}
